/**
 * The private Brand Bible write capability.
 *
 * This is the only module in the WebMCP surface that imports the Brand Bible
 * store. Every trap a programmatic writer hits against that store is contained
 * here, so a command author writes a `resolve`/`diff`/`run` triple and never
 * learns any of it.
 *
 * The seam is the Zustand store, not the section save registry and not
 * `useBbDraftCommit()`. Both of those are React only and unreachable from a
 * tool. `brandBibleWizardStore.ts:310` builds the store with `create()`, so
 * `getState()` works with no component mounted.
 *
 *     await getState().awaitLoaded(tenantId)                    // :587
 *     await getState().upsertEntry(table, payload, id?)         // :607
 *
 * WHAT IS ABSENT, AND WHY THE ABSENCE IS THE MECHANISM
 *
 * There is exactly one exported method, `commit`. There is no verify, no
 * unverify, no public approval, no delete, no discard, no client switch, no
 * tenant settings, and no legacy whole bible PUT. None of those are documented
 * as forbidden. They simply do not exist here, and this module is the only
 * thing holding the store handle, so no command can reach them by construction.
 *
 * Three of those omissions are load bearing and worth naming.
 *
 *  - `verifyEntry` defaults `approveForPublic` to false
 *    (`brandBibleWizardStore.ts:658`), and since commit `7a567f536` that flag
 *    gates projection into generated content (`projection.py:218-221`). A
 *    re-verify of an already public row silently demotes it and removes a
 *    claim from every future article.
 *  - The legacy PUT is guarded by `assert_legacy_writable`, which only raises
 *    for tenants that already have a live `bb_identity` row
 *    (`activation.py:13`, `:28`). A tenant without one has no guard at all and
 *    the PUT overwrites the entire flat row.
 *  - Tenant settings carry WordPress passwords and API tokens, so this
 *    capability has no dependency path to them at all. `Receipt.facts` is
 *    primitive only, which is necessary and not sufficient, because secrets
 *    are also strings.
 *
 * Section field schemas are a separate blocked ticket. The patch arrives here
 * already validated by the command's own zod schema and is treated as an
 * opaque record. Nothing below invents a per section field list. What it does
 * enforce is the small set of key names that are dangerous on every table.
 */
import { useBrandBibleWizardStore } from '@/stores/brandBibleWizardStore';
import type { BbTable } from '@/features/brand-bible-wizard/types';
import {
  computePatchDigest,
  readBaseVersion,
  refuse as refusalValue,
  type CommitAuthorization,
  type Receipt,
  type Refusal,
  type WriteDeps,
} from './contract';

// ---------------------------------------------------------------------------
// The store port
// ---------------------------------------------------------------------------

/** One row as this module needs to see it. Deliberately structural. */
export interface BbRow {
  readonly id: string;
  readonly version: number;
  readonly deleted_at: string | null;
  readonly [column: string]: unknown;
}

/**
 * The subset of the wizard store this capability is allowed to touch.
 *
 * This is a narrowing, not a convenience. `verifyEntry`, `unverifyEntry`,
 * `discardEntry` and `runEnrichment` all exist on the real store and are
 * omitted here, so nothing downstream of `createBrandBibleWriter` can call
 * them even with a handle to the port.
 */
export interface BrandBibleStoreSlice {
  /** The REQUESTED tenant. Flips the instant `init()` is called. */
  readonly tenantId: string | null;
  /** The tenant whose `entries` are currently valid (`:40`). */
  readonly loadedTenantId: string | null;
  readonly entries: Readonly<Partial<Record<BbTable, readonly BbRow[]>>>;
  init(tenantId: string): Promise<void>;
  awaitLoaded(tenantId: string, timeoutMs?: number): Promise<boolean>;
  upsertEntry(
    table: BbTable,
    payload: Readonly<Record<string, unknown>>,
    id: string | undefined,
    tenantId: string,
  ): Promise<BbRow | null>;
  /**
   * Optional, and today never present. `brand_bible_v2.py:119` puts
   * `projection_error: true` in the mutation response body when the in band
   * projection fails after the write has already landed. `MutationResponse`
   * (`types.ts:256`) declares only `entry`, `score` and `warning`, and
   * `applyMutation` (`brandBibleWizardStore.ts:289`) keeps only those three,
   * so the flag is discarded before any caller can see it. Widening the store
   * to carry it is an open adapter ask. Until then `Receipt.projectionFailed`
   * is omitted rather than guessed at.
   */
  lastProjectionError?(): boolean | undefined;
}

/** Re-read on every use. `awaitLoaded` may run an `init()` that replaces state. */
export type BrandBibleStoreAccess = () => BrandBibleStoreSlice;

// ---------------------------------------------------------------------------
// Failure vocabulary
// ---------------------------------------------------------------------------

/**
 * `refused` means nothing was sent. `failed` means the server gave a real
 * verdict and the write did not land. `unknown` means we never learned the
 * server's decision, so the write may or may not have landed.
 */
export type WriteOutcome = 'refused' | 'failed' | 'unknown';

/**
 * Every non success exits through this. `commit` never resolves with a value
 * that means "nothing happened", because the store's own null return is
 * exactly that shape and it is the trap this module exists to remove.
 */
export class BrandBibleWriteError extends Error {
  readonly outcome: WriteOutcome;
  readonly because: string;
  /**
   * False for `unknown` on purpose. An unknown outcome must be re-read before
   * anything is sent again, never retried blindly.
   */
  readonly retryable: boolean;
  readonly facts: Readonly<Record<string, string | number | boolean>>;
  readonly cause?: unknown;

  constructor(
    outcome: WriteOutcome,
    because: string,
    facts: Readonly<Record<string, string | number | boolean>> = {},
    cause?: unknown,
  ) {
    super(`brand bible commit ${outcome}. ${because}`);
    this.name = 'BrandBibleWriteError';
    this.outcome = outcome;
    this.because = because;
    this.retryable = false;
    this.facts = facts;
    this.cause = cause;
    Object.setPrototypeOf(this, BrandBibleWriteError.prototype);
  }

  /**
   * The value form. Every non-success leaves `commit` through this.
   *
   * Thrown internally and returned at the boundary on purpose. The screening
   * helpers below are nested several deep and an early exit from `canonicalize`
   * or `checkBaseVersion` reads far better as a throw, but a caller must not
   * have to tell a real refusal apart from a genuine fault, so the exception
   * never crosses the module edge.
   *
   * `facts` is folded into the text because `Refusal` has no structured slot
   * and dropping it would lose the row id and version that make a refusal
   * actionable. `retryable` stays false for all three outcomes: `refused` and
   * `failed` need the operator to change something, and `unknown` must be
   * re-read before anything is sent again rather than retried blindly.
   */
  toRefusal(): Refusal {
    const detail = Object.keys(this.facts)
      .sort()
      .map((key) => `${key}=${String(this.facts[key])}`)
      .join(', ');
    return refusalValue(
      detail.length > 0 ? `${this.because} (${detail})` : this.because,
      { retryable: this.retryable },
    );
  }
}

function refuse(
  because: string,
  facts: Readonly<Record<string, string | number | boolean>> = {},
): never {
  throw new BrandBibleWriteError('refused', because, facts);
}

// ---------------------------------------------------------------------------
// Static policy
// ---------------------------------------------------------------------------

/**
 * The eleven v2 entry tables (`types.ts:7`). An `auth.table` outside this list
 * is refused before anything else runs, which is what stops a string such as
 * `tenant_settings` or `brand_bible` from ever reaching a service call.
 */
const WRITABLE_TABLES: readonly string[] = [
  'identity',
  'voice_profile',
  'services',
  'locations',
  'credentials',
  'offers',
  'review_stats',
  'case_studies',
  'compliance_rules',
  'visual_identity',
  'chunks',
];

/** Coalescing create key is shared for these, so two creates merge (`:128`). */
const SINGLETON_TABLES: readonly string[] = ['identity', 'voice_profile', 'visual_identity'];

/**
 * Key names that are dangerous on every table. This is a deny list of known
 * traps, not an allow list of section fields. Each entry states its own reason
 * so a refusal teaches the command author rather than just blocking them.
 */
const FORBIDDEN_KEYS: Readonly<Record<string, string>> = {
  approved_for_public:
    'public approval is not part of this capability, and the flag gates whether a claim reaches generated content (projection.py:218-221)',
  verified_by: 'verification is not patchable and 422s (entries.py:372, registry.py:10)',
  verified_at: 'verification is not patchable and 422s (entries.py:372, registry.py:10)',
  deleted_at: 'this capability has no delete',
  source:
    'the server records source=intake for every write from here (entries.py:358). Client set provenance is a backend ask, not a field',
  confidence: 'server owned (entries.py:360)',
  id: 'server owned',
  tenant_id: 'the tenant comes from the authorization, never from the patch',
  version: 'server owned optimistic-lock counter',
  created_at: 'server owned',
  updated_at: 'server owned',
  real_photos_only: 'derived and read only in the UI (VisualIdentityPanel.tsx:11)',
  service_locations:
    'a page owned diff-sync, not a column. A short array SOFT DELETES the missing rows (BrandBiblePage.tsx:1228-1244). Write bb_locations rows one at a time instead',
  services_offered:
    'a page owned diff-sync, not a column. A short array SOFT DELETES the missing rows (BrandBiblePage.tsx:1304). Write bb_services rows one at a time instead',
  business_name: 'flat legacy column owned by section A. Write bb_identity.legal_name instead',
  description: 'flat legacy column owned by section A. Write bb_identity.brand_overview instead',
  contact_info: 'flat legacy column owned by section A. Write bb_identity.phone / .email instead',
  social_media_links:
    'flat legacy column, overwritten by the projection from bb_identity.socials. Write bb_identity.socials instead',
  competitors: 'flat legacy column owned by section F, and a write only mirror of the link policy table',
  brand_restrictions: 'flat legacy column owned by section G, and it ships whole or not at all',
  customer_pain_points: 'flat legacy column owned by section E, raced by the page autosave',
  website_audience: 'flat legacy column owned by section E, raced by the page autosave',
  buyer_personas: 'flat legacy column owned by section E, raced by the page autosave',
  __proto__: 'prototype pollution',
  constructor: 'prototype pollution',
  prototype: 'prototype pollution',
};

/**
 * JSONB merge columns (`registry.py:24-25`). The UPDATE path emits
 * `jsonb_strip_nulls(COALESCE(col, '{}'::jsonb) || CAST(:col AS jsonb))`
 * (`entries.py:325`) and binds `json.dumps(value)`, so a top level `null`
 * binds the literal `'null'`. In Postgres `'{}'::jsonb || 'null'::jsonb` does
 * not merge, it CONCATENATES an object with a scalar and yields an ARRAY. The
 * column silently becomes `[null]`. A `null` is legal only as a VALUE inside
 * the object, where it deletes that key (`types.ts:55`).
 */
const MERGE_COLUMNS: readonly string[] = ['owner', 'socials', 'hours'];

/**
 * `bb_locations` rows keyed `'primary'` project the literal string "primary"
 * into the flat `service_locations` array. Feeding a display array back would
 * "otherwise upsert a bogus metro='primary' row" (`BrandBiblePage.tsx:358`).
 * The match is exact, trimmed and case insensitive, so a real city containing
 * the word survives.
 */
const LOCATION_SENTINEL = 'primary';

function isLocationSentinel(value: unknown): boolean {
  return typeof value === 'string' && value.trim().toLowerCase() === LOCATION_SENTINEL;
}

// ---------------------------------------------------------------------------
// Wire safety
//
// The patch digest itself now lives in `contract.ts` and is shared with the
// runtime that mints the authorization. This module used to export its own
// async SHA-256 over a locally canonicalized patch while the runtime hashed a
// differently canonicalized one, so the two sides of the binding compared
// different numbers and every write would have refused.
//
// The digest is a pure function and cannot refuse, so the value checks that
// used to live inside the local canonicalizer are here instead, where a
// refusal is available and can name the offending field.
// ---------------------------------------------------------------------------

/**
 * Refuses anything the digest and the wire would disagree about.
 *
 * `undefined` is dropped by `JSON.stringify` rather than encoded, a non-finite
 * number serializes as `null`, and a function, symbol or bigint either
 * vanishes or throws. Each would let a digest match a payload that does not
 * carry the same values, so each is refused by name rather than normalized.
 * Recursive, because the merge columns hold nested objects.
 */
function assertWireSafe(value: unknown, path: string): void {
  if (value === null) return;

  if (typeof value === 'undefined') {
    refuse(`patch field ${path} is undefined, which JSON drops, so the digest and the wire would disagree`);
  }
  if (typeof value === 'number' && !Number.isFinite(value)) {
    refuse(`patch field ${path} is not a finite number`);
  }
  if (typeof value === 'function' || typeof value === 'symbol' || typeof value === 'bigint') {
    refuse(`patch field ${path} has type ${typeof value}, which cannot cross the wire`);
  }

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) assertWireSafe(value[i], `${path}[${i}]`);
    return;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i] as string;
      assertWireSafe(record[key], `${path}.${key}`);
    }
  }
}

// ---------------------------------------------------------------------------
// Comparison, matching the store's own tolerance for jsonb shaped values
// ---------------------------------------------------------------------------

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a === 'undefined' || typeof b === 'undefined') return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => valuesEqual(v, b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ak = Object.keys(a as object);
    const bk = Object.keys(b as object);
    if (ak.length !== bk.length) return false;
    return ak.every((k) =>
      valuesEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
    );
  }
  return false;
}

/**
 * Second chance for the post write comparison only.
 *
 * Several columns are typed on the server and returned in their stored type,
 * so `founded_year` sent as "1998" comes back as 1998 and a NUMERIC(3,2)
 * rating comes back with its own precision. Without this a landed write would
 * be reported as a definite failure, which is the worst outcome available
 * because the agent then believes it must write again. Scalars only, so it can
 * never mask a structural divergence.
 */
function coercedEqual(a: unknown, b: unknown): boolean {
  const scalar = (v: unknown): boolean =>
    typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
  if (!scalar(a) || !scalar(b)) return false;
  return String(a) === String(b);
}

// ---------------------------------------------------------------------------
// Error classification
// ---------------------------------------------------------------------------

interface AxiosShaped {
  code?: string;
  message?: string;
  response?: { status?: number };
  unpersistedFields?: Record<string, unknown>;
}

const UNKNOWN_CODES: readonly string[] = ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK', 'ERR_CANCELED'];
const UNKNOWN_STATUSES: readonly number[] = [408, 502, 503, 504];

/**
 * Mirrors `isOutcomeUnknown` (`brandBibleWizardStore.ts:212`) and adds the
 * three reconcile messages the store itself throws.
 *
 * The store already reconciles an unknown outcome by refetching, so most of
 * these surface from `awaitLoaded`/`init` rather than from the write. What
 * still escapes as unknown is a reconcile that could not run, and a "row not
 * found" or "did not persist" verdict on a CREATE, because
 * `findByBestEffortKey` (`:246`) can match the wrong row when two rows share a
 * name. The same verdict on a PATCH is definite, because the id match is exact.
 */
function classify(err: unknown, hasExactRowId: boolean): { outcome: 'failed' | 'unknown'; because: string } {
  const e = (err ?? {}) as AxiosShaped;
  const message = typeof e.message === 'string' ? e.message : String(err);

  if (typeof e.code === 'string' && UNKNOWN_CODES.indexOf(e.code) >= 0) {
    return { outcome: 'unknown', because: `transport gave up with ${e.code}, so the write may or may not have landed` };
  }
  const status = e.response && typeof e.response.status === 'number' ? e.response.status : undefined;
  if (typeof status === 'number' && UNKNOWN_STATUSES.indexOf(status) >= 0) {
    return { outcome: 'unknown', because: `gateway returned ${status}, which is not the application's verdict` };
  }
  if (/timeout of \d+ms exceeded/i.test(message)) {
    return { outcome: 'unknown', because: 'client timeout, so the write may or may not have landed' };
  }
  if (/could not be reconciled/i.test(message)) {
    return { outcome: 'unknown', because: 'the write timed out and the reconcile refetch also failed' };
  }
  if (/row not found after reconcile|did not persist/i.test(message)) {
    if (!hasExactRowId) {
      return {
        outcome: 'unknown',
        because:
          'a created row could not be matched back by best effort key, which can match the wrong row. Re-read before sending anything again',
      };
    }
    return { outcome: 'failed', because: message };
  }
  return { outcome: 'failed', because: message };
}

// ---------------------------------------------------------------------------
// Per row single flight
//
// ONE USE IS NOT ENFORCED HERE, and deliberately so. A `WeakSet` in this file
// used to hold the authorizations it had seen, which proved membership in this
// module rather than in the runtime's registry: any in-process object with the
// exported shape and a matching digest passed it. `runtime.wrapWriteSeams` now
// spends the real record against that registry immediately before this
// capability is called, so a second file tracking the same fact could only
// disagree with it.
// ---------------------------------------------------------------------------

const inFlight = new Set<string>();

function flightKey(auth: CommitAuthorization): string | null {
  if (auth.rowId !== null) return `${auth.tenant}:${auth.table}:${auth.rowId}`;
  // Non singleton creates each get their own store key (`:164`), so two of
  // them are safe to run at once. Singleton creates deliberately share one key
  // and coalesce, which would make the post write verification below ambiguous.
  if (SINGLETON_TABLES.indexOf(auth.table) >= 0) return `${auth.tenant}:${auth.table}:CREATE`;
  return null;
}

// ---------------------------------------------------------------------------
// The capability
// ---------------------------------------------------------------------------

export function createBrandBibleWriter(access: BrandBibleStoreAccess): WriteDeps {
  /** Everything that can be decided without touching the store or the network. */
  function screen(
    auth: CommitAuthorization,
    patch: Readonly<Record<string, unknown>>,
  ): void {
    if (!(typeof auth.expiresAt === 'number') || Date.now() > auth.expiresAt) {
      refuse('the authorization has expired. Re-confirm with the operator');
    }
    if (WRITABLE_TABLES.indexOf(auth.table) < 0) {
      refuse(`${auth.table} is not a Brand Bible v2 entry table`, { table: auth.table });
    }
    if (typeof auth.tenant !== 'string' || auth.tenant.length === 0) {
      refuse('the authorization carries no tenant');
    }

    const keys = Object.keys(patch);
    if (keys.length === 0) refuse('the patch is empty');

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i] as string;
      const forbidden = Object.prototype.hasOwnProperty.call(FORBIDDEN_KEYS, key)
        ? FORBIDDEN_KEYS[key]
        : undefined;
      if (typeof forbidden === 'string') {
        refuse(`the patch sets ${key}, which this capability never writes. ${forbidden}`, { field: key });
      }

      const value = patch[key];
      if (typeof value === 'undefined') {
        refuse(`the patch sets ${key} to undefined. Send null to clear a nullable scalar instead`, { field: key });
      }
      assertWireSafe(value, key);

      if (MERGE_COLUMNS.indexOf(key) >= 0) {
        if (value === null) {
          refuse(
            `${key} is a JSONB merge column, so a top level null concatenates rather than merges and turns the column into an array. Send an object, and use null on an inner key to delete that key`,
            { field: key },
          );
        }
        if (Array.isArray(value) || typeof value !== 'object') {
          refuse(`${key} is a JSONB merge column and must be a plain object`, { field: key });
        }
      }

      if (key === 'location_key' && isLocationSentinel(value)) {
        refuse(
          "'primary' is the internal location sentinel. It leaks into the flat service_locations array as a literal string, and writing it back upserts a bogus row",
          { field: key },
        );
      }
    }

    // The runtime minted `auth.patchDigest` with this same function over the
    // rendered diff. One implementation, so this is a real binding check and
    // not a comparison of two formatting conventions.
    const digest = computePatchDigest(patch);
    if (digest !== auth.patchDigest) {
      refuse(
        'the patch does not match the approved one. An approval binds to one exact tenant, table, row and patch',
      );
    }
  }

  /**
   * Settles the store on the authorized tenant and forces one fresh read.
   *
   * `awaitLoaded` short-circuits when `loadedTenantId` already matches
   * (`:588`), so it does NOT refresh `lastKnownFieldValues`. That matters
   * twice over. A stale known value silently suppresses this write
   * (`:617-634`), and a stale `version` makes the base version check
   * meaningless. `init()` is the only refresh, and it is same tenant safe by
   * design (`:517-528`) because it swaps atomically rather than wiping.
   */
  async function settle(auth: CommitAuthorization): Promise<void> {
    const before = access();
    if (before.tenantId !== null && before.tenantId !== auth.tenant) {
      // Never yank the store onto another tenant. An operator may be part way
      // through an edit, and the section buffers are not cleared by a switch.
      refuse(
        `the app is on tenant ${before.tenantId} and this write is authorized for ${auth.tenant}. Switch the workspace first`,
        { store_tenant: before.tenantId, authorized_tenant: auth.tenant },
      );
    }

    let loaded = false;
    try {
      loaded = await access().awaitLoaded(auth.tenant);
    } catch (err) {
      const c = classify(err, false);
      throw new BrandBibleWriteError('unknown', `the brand bible load failed. ${c.because}`, {}, err);
    }
    if (!loaded) {
      // The store's own contract at `:47` says callers must treat false as "do
      // not proceed", never as "proceed anyway".
      refuse(`the brand bible did not load for ${auth.tenant} within the store's bound`, {
        tenant: auth.tenant,
      });
    }

    if (access().tenantId !== auth.tenant) {
      refuse('the tenant changed while the brand bible was loading');
    }

    try {
      await access().init(auth.tenant);
    } catch (err) {
      throw new BrandBibleWriteError(
        'unknown',
        'the forced pre-write refresh failed, so neither the base version nor duplicate suppression can be trusted',
        {},
        err,
      );
    }

    const after = access();
    if (after.loadedTenantId !== auth.tenant || after.tenantId !== auth.tenant) {
      refuse('the tenant changed during the pre-write refresh');
    }
  }

  function currentRow(auth: CommitAuthorization): BbRow | null {
    if (auth.rowId === null) return null;
    const table = auth.table as BbTable;
    const rows = access().entries[table];
    if (!rows) return null;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i] as BbRow;
      if (row.id === auth.rowId) return row;
    }
    return null;
  }

  /**
   * A singleton create authorized before a web gather landed: the forced refresh just read the
   * table, so a live unconfirmed draft here means the create would sit beside, or shadow, a row a
   * person has not reviewed yet. Refuse it rather than create a second singleton.
   */
  function checkNoLiveSingletonDraft(auth: CommitAuthorization): void {
    if (auth.rowId !== null || SINGLETON_TABLES.indexOf(auth.table) < 0) return;
    const rows = (access().entries[auth.table as BbTable] ?? []) as readonly BbRow[];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i] as BbRow & { source?: unknown; verified_by?: unknown };
      if (row.deleted_at === null && row.source === 'discovered' && (row.verified_by === null || row.verified_by === undefined)) {
        refuse(
          `${auth.table} already has an unconfirmed draft (row ${row.id}) found on the web. A person has to confirm it in the Brand Bible page before ${auth.table} can be written here`,
          { row_id: row.id },
        );
      }
    }
  }

  /** Read before write, against the row the forced refresh just produced. */
  function checkBaseVersion(auth: CommitAuthorization, row: BbRow | null): void {
    if (auth.rowId === null) {
      if (auth.baseVersion !== null) {
        refuse('a create carries no base version, so the authorization is malformed');
      }
      return;
    }
    if (row === null) {
      refuse(`row ${auth.rowId} is not present in ${auth.table} after a fresh read`, { row_id: auth.rowId });
    }
    if (row.deleted_at !== null) {
      refuse(`row ${auth.rowId} is deleted, and this capability never resurrects a row`, {
        row_id: auth.rowId,
      });
    }
    if (auth.baseVersion === null) {
      // Deliberately a refusal and not a warning. `CommitAuthorization`
      // carries `baseVersion` precisely so an update is a read before write,
      // and there is no server side compare and swap on brand bible entries to
      // fall back on.
      refuse('the authorization carries no base version, so a concurrent editor could be overwritten silently');
    }
    // An unconfirmed web draft (discovered, no verifier) is changed only by a person in the
    // Brand Bible page, never by an agent: confirming it is the human step.
    const draft = row as { source?: unknown; verified_by?: unknown };
    if (draft.source === 'discovered' && (draft.verified_by === null || draft.verified_by === undefined)) {
      refuse(
        `row ${auth.rowId} is an unconfirmed draft found on the web. A person has to confirm it in the Brand Bible page before it can be changed here`,
        { row_id: auth.rowId },
      );
    }
    // Through `readBaseVersion` and never `row.version` directly. The runtime
    // minted `auth.baseVersion` with that same function, and a `BbRow` carries
    // both `version` and `updated_at`, so reading one field here and letting
    // the runtime pick the other is how every update came to refuse.
    const current = readBaseVersion(row);
    if (current !== auth.baseVersion) {
      refuse(
        `row ${auth.rowId} moved from version ${auth.baseVersion} to ${current ?? 'unknown'} since the operator approved. Re-read and re-confirm`,
        {
          row_id: auth.rowId,
          approved_version: auth.baseVersion,
          current_version: current ?? 'unknown',
        },
      );
    }
  }

  /**
   * `entries.py:381` force-overwrites `public_mention` to false on a
   * competitor chunk. That is a silent coercion rather than an error, so an
   * agent that sets it would be told the write succeeded while the value it
   * asked for was discarded.
   */
  function checkSilentCoercions(
    auth: CommitAuthorization,
    patch: Readonly<Record<string, unknown>>,
    row: BbRow | null,
  ): void {
    if (auth.table !== 'chunks') return;
    const kind = 'kind' in patch ? patch['kind'] : row === null ? undefined : row['kind'];
    if (kind === 'competitor' && patch['public_mention'] === true) {
      refuse(
        'a competitor chunk is internal by force and the server silently rewrites public_mention to false (entries.py:381), so this write would report a value it did not store',
        { field: 'public_mention' },
      );
    }
  }

  /**
   * Confirms the server row actually carries what was sent. Merge columns are
   * checked key by key, because the server merged rather than replaced and a
   * null inner value means the key was deleted.
   */
  function unpersistedFields(patch: Readonly<Record<string, unknown>>, row: BbRow): string[] {
    const missed: string[] = [];
    const keys = Object.keys(patch);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i] as string;
      const wanted = patch[key];
      const got = row[key];

      if (MERGE_COLUMNS.indexOf(key) >= 0 && wanted !== null && typeof wanted === 'object') {
        const merged = (got === null || typeof got !== 'object' ? {} : got) as Record<string, unknown>;
        const inner = wanted as Record<string, unknown>;
        const innerKeys = Object.keys(inner);
        for (let j = 0; j < innerKeys.length; j++) {
          const ik = innerKeys[j] as string;
          const iv = inner[ik];
          const present = Object.prototype.hasOwnProperty.call(merged, ik);
          if (iv === null) {
            // jsonb_strip_nulls removes the key rather than storing null.
            if (present && merged[ik] !== null) missed.push(`${key}.${ik}`);
          } else if (!present || !valuesEqual(merged[ik], iv)) {
            missed.push(`${key}.${ik}`);
          }
        }
        continue;
      }

      if (!valuesEqual(got, wanted) && !coercedEqual(got, wanted)) missed.push(key);
    }
    return missed;
  }

  async function attempt(
    auth: CommitAuthorization,
    patch: Readonly<Record<string, unknown>>,
  ): Promise<Receipt> {
    screen(auth, patch);

    const key = flightKey(auth);
    if (key !== null && inFlight.has(key)) {
      refuse(
        'another commit for this exact row is still in flight. The store would coalesce the two payloads into one write and neither result could be verified',
      );
    }
    if (key !== null) inFlight.add(key);

    try {
      await settle(auth);

      const before = currentRow(auth);
      checkBaseVersion(auth, before);
      checkNoLiveSingletonDraft(auth);
      checkSilentCoercions(auth, patch, before);

      const table = auth.table as BbTable;
      const rowId = auth.rowId === null ? undefined : auth.rowId;
      const priorVersion = before === null ? null : before.version;

      // No await between reading the slice and calling upsertEntry.
      // `upsertEntry` reads `get().tenantId` synchronously at its top
      // (`:608`), so this is the tick in which the tenant is guaranteed.
      const slice = access();
      if (slice.tenantId !== auth.tenant || slice.loadedTenantId !== auth.tenant) {
        refuse('the tenant changed immediately before the write');
      }

      // Expiry, re-read in the tick the request is issued. `screen` checked it
      // before `settle`, and settling forces a full brand bible load that can
      // outlast the whole TTL, so the earlier check says nothing about now. The
      // write boundary spent this authorization before the call, which is what
      // makes it one use; this is the one fact that can still change underneath
      // it while this function does I/O.
      if (Date.now() > auth.expiresAt) {
        refuse(
          'the authorization expired while the store was settling, so it was not sent. Re-confirm with the operator',
        );
      }

      let row: BbRow | null;
      try {
        row = await slice.upsertEntry(table, patch, rowId, auth.tenant);
      } catch (err) {
        const c = classify(err, auth.rowId !== null);
        throw new BrandBibleWriteError(c.outcome, c.because, { table: auth.table }, err);
      }

      if (row === null) {
        // `upsertEntry` resolves `Promise.resolve(null)` when the store has
        // no tenant (`:609`). It does not throw, and `verifyEntry`,
        // `unverifyEntry`, `discardEntry` and `runEnrichment` all bail the
        // same way. The other null path is a singleton create whose shared
        // CREATE key still holds known values from earlier in the session,
        // which suppresses the payload and leaves no row to hand back.
        // Either way a null is never a success.
        throw new BrandBibleWriteError(
          'failed',
          'the store returned null, so the write was never issued. Either the store has no tenant, or every field was suppressed as already-known on a create with no row to return',
          { table: auth.table },
        );
      }

      if (row.deleted_at !== null) {
        throw new BrandBibleWriteError('failed', `row ${row.id} came back deleted`, { row_id: row.id });
      }

      const missed = unpersistedFields(patch, row);
      if (missed.length > 0) {
        throw new BrandBibleWriteError(
          'failed',
          `${missed.join(', ')} did not persist`,
          { table: auth.table, row_id: row.id, unpersisted: missed.join(',') },
        );
      }

      // A version that did not move means `upsertEntry` diffed the payload
      // against `lastKnownFieldValues` and returned the current row without
      // touching the network (`:617-634`). Because `settle` forced a fresh
      // read first, that is a truthful statement about the server and not a
      // stale cache, but it is still not a write and must not be reported as
      // one.
      const changed = priorVersion === null || row.version !== priorVersion;

      const probe = access().lastProjectionError;
      const projectionFailed = typeof probe === 'function' ? probe() : undefined;

      const receipt: Receipt = {
        summary: changed
          ? `${auth.rowId === null ? 'Created' : 'Updated'} ${auth.table} row ${row.id} for ${auth.tenant}.`
          : `${auth.table} row ${row.id} already matched the requested values, so nothing was sent.`,
        facts: {
          tenant: auth.tenant,
          table: auth.table,
          row_id: row.id,
          changed,
          fields: Object.keys(patch).length,
          version: row.version,
          // Honest about the gap rather than defaulting to false, which
          // would read as "the projection succeeded".
          projection_status: typeof projectionFailed === 'boolean' ? 'observed' : 'unobservable',
        },
        ...(typeof projectionFailed === 'boolean' ? { projectionFailed } : {}),
      };

      return receipt;
    } finally {
      if (key !== null) inFlight.delete(key);
    }
  }

  return {
    async commit(
      auth: CommitAuthorization,
      patch: Readonly<Record<string, unknown>>,
    ): Promise<Receipt | Refusal> {
      try {
        return await attempt(auth, patch);
      } catch (err) {
        // SEAM 4. Refusals are values at this boundary. Only a genuine bug
        // rethrows, and the runtime encodes that as its unexpected-error path.
        if (err instanceof BrandBibleWriteError) return err.toRefusal();
        throw err;
      }
    },
  };

}

/**
 * The single binding to the real store, and the only place in the WebMCP
 * surface that holds it.
 *
 * The cast is `unknown` because the store's `upsertEntry` is generic over
 * `BbTable` and its payload type is a per table union, which a runtime
 * validated opaque record cannot satisfy nominally. It widens nothing
 * dangerous. `BrandBibleStoreSlice` is a strict subset of the store's surface
 * with `verifyEntry`, `unverifyEntry`, `discardEntry` and `runEnrichment`
 * omitted, so the writer cannot call them even by mistake.
 */
export const brandBibleWriter: WriteDeps = createBrandBibleWriter(
  () => useBrandBibleWizardStore.getState() as unknown as BrandBibleStoreSlice,
);

/** Exported for the confirmation step, which must not reach the store itself. */
export const __policy = Object.freeze({
  WRITABLE_TABLES,
  FORBIDDEN_KEYS,
  MERGE_COLUMNS,
  LOCATION_SENTINEL,
});
